import {
  AlertCircle,
  AlertTriangle,
  ArrowUpCircle,
  CircleDashed,
  Clock,
  FileCheck,
  FileText,
  HelpCircle,
  Loader2,
  Sparkles,
} from "lucide-react";

import { statusLabel, type LessonStatus } from "@/lib/status";

/**
 * Per-lesson recording indicator, PLAN.md §8. The colours are the ones the plan
 * names, hard-coded rather than themed: they carry meaning, so they must not
 * shift with the palette.
 */
export default function StatusIcon({
  status = "no_recording",
  partialAudio = false,
  needsReview = false,
  className = "",
}: {
  status?: LessonStatus | string;
  partialAudio?: boolean;
  /** A weak automatic match, PLAN.md §9. Marked, never hidden. */
  needsReview?: boolean;
  className?: string;
}) {
  const label = statusLabel(status);
  const notes = [
    partialAudio ? "audio has gaps" : null,
    needsReview ? "check this match" : null,
  ].filter((note) => note !== null);
  const title = notes.length === 0 ? label : `${label} · ${notes.join(" · ")}`;
  return (
    <span
      className={`relative inline-flex items-center ${className}`}
      title={title}
      aria-label={title}
    >
      <Glyph status={status} />
      {partialAudio && (
        <AlertTriangle
          className="absolute -right-1.5 -top-1.5 h-2.5 w-2.5 text-[#F59E0B]"
          aria-hidden
        />
      )}
      {needsReview && !partialAudio && (
        <HelpCircle
          className="absolute -right-1.5 -top-1.5 h-2.5 w-2.5 text-[#F59E0B]"
          aria-hidden
        />
      )}
    </span>
  );
}

function Glyph({ status }: { status?: string }) {
  switch (status) {
    case "recording":
      return (
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#EF4444] opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#EF4444]" />
        </span>
      );
    case "uploading":
    case "stored":
      return <ArrowUpCircle className="h-3.5 w-3.5 text-[#F59E0B]" />;
    case "queued":
      return <Clock className="h-3.5 w-3.5 text-[#F59E0B]" />;
    case "transcribing":
      return <Loader2 className="h-3.5 w-3.5 animate-spin text-[#3B82F6]" />;
    case "transcribed":
      return <FileText className="h-3.5 w-3.5 text-[#3B82F6]" />;
    case "summarizing":
      return <Sparkles className="h-3.5 w-3.5 animate-pulse text-[#8B5CF6]" />;
    case "done":
      return <FileCheck className="h-3.5 w-3.5 text-[#22C55E]" />;
    case "failed":
      return <AlertCircle className="h-3.5 w-3.5 text-[#DC2626]" />;
    case "no_recording":
    default:
      return <CircleDashed className="h-3.5 w-3.5 text-[#9CA3AF]" />;
  }
}
